import { useCallback, useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DiscountAtCheckout } from '../../interfaces/DiscountAtCheckout/DiscountAtCheckout';
import { updateDiscountsAtCheckout } from '../../api/DiscountAtCheckout';
import { Waiting } from '../../components/Waiting/Waiting';
import { sharedColors, sharedStyles } from '../../utils/Style';
import { ProductsDropdown } from '../../components/Dropdowns/ProductsDropdown';
import NumberInput from '../../components/NumberInput/NumberInput';

type UpsertDiscountAtCheckoutDialogProps = {
  open: boolean;
  onClose: () => void;
  onUpsert: () => void;
  existingDiscounts: DiscountAtCheckout[];
  clickedDiscount?: DiscountAtCheckout;
};

export const UpsertDiscountAtCheckoutDialog = ({
  open,
  onClose,
  onUpsert,
  existingDiscounts,
  clickedDiscount,
}: UpsertDiscountAtCheckoutDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [productID, setProductID] = useState<number | undefined>(undefined);
  const [price, setPrice] = useState(0);

  useEffect(() => {
    setProductID(clickedDiscount?.id);
    setPrice(clickedDiscount?.price ?? 0);
  }, [clickedDiscount, open]);

  const handleConfirm = useCallback(() => {
    if (!productID || !price) return;

    setLoading(true);
    updateDiscountsAtCheckout([
      { id: productID, name: '', price },
      ...existingDiscounts.filter((d) => d.id !== productID),
    ])
      .then(onUpsert)
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [productID, price, clickedDiscount]);

  const validState = !!productID && !!price;

  return (
    <Dialog open={open} onClose={onClose}>
      <Waiting open={loading} />

      <DialogTitle sx={{ ...sharedStyles.h6, color: sharedColors.gray6 }}>
        {clickedDiscount ? 'Ürünü Düzenle' : 'Yeni Ürün'}
      </DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          width: 400,
        }}
      >
        <Box />

        {clickedDiscount ? (
          <Tooltip title={clickedDiscount.name}>
            <TextField
              disabled
              size='small'
              value={clickedDiscount.name}
              fullWidth
            />
          </Tooltip>
        ) : (
          <ProductsDropdown
            label='Ürün'
            multiple={false}
            excludedIDs={existingDiscounts.map((x) => x.id)}
            value={productID}
            onChange={setProductID}
          />
        )}

        <NumberInput
          label='Kasa Önü Fiyatı'
          value={price}
          onChange={setPrice}
          acceptFloat
          error={!price}
          endAdornment={<Typography>₺</Typography>}
        />
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 1.5 }}>
        <Button
          variant='contained'
          color='inherit'
          onClick={onClose}
          sx={sharedStyles.buttonText}
        >
          İptal
        </Button>
        <Button
          variant='contained'
          color='primary'
          disabled={!validState}
          onClick={handleConfirm}
          sx={sharedStyles.buttonText}
        >
          Onayla
        </Button>
      </DialogActions>
    </Dialog>
  );
};
